import { useTranslation } from "react-i18next";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { OrderAtlasConfig, type AtlasConfig } from "./OrderAtlasConfig";
import { OrderValidConfig, type ValidConfig } from "./OrderValidConfig";
import { OrderUserDetails, type UserDetail } from "./OrderUserDetails";
import type { ContactInfo, CompanyInfo, BillingInfo } from "./OrderCustomerForm";
import { CheckCircle, User, Building2, FileText, Package, Users, Pencil, Check } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Copy, Info } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/hooks/useLanguage";
import { cn } from "@/lib/utils";

export type EditableSection = "config" | "users" | "contact" | "company" | "billing" | null;

interface Props {
  product: "atlas" | "valid";
  atlasConfig: AtlasConfig;
  validConfig: ValidConfig;
  contact: ContactInfo;
  company: CompanyInfo;
  billing: BillingInfo;
  adminUsers: UserDetail[];
  viewerUsers: UserDetail[];
  showTerms?: boolean;
  termsAccepted?: boolean;
  onTermsChange?: (accepted: boolean) => void;
  // Inline editing
  editingSection?: EditableSection;
  onEditSection?: (section: EditableSection) => void;
  onAtlasConfigChange?: (config: AtlasConfig) => void;
  onValidConfigChange?: (config: ValidConfig) => void;
  onAdminUsersChange?: (users: UserDetail[]) => void;
  onViewerUsersChange?: (users: UserDetail[]) => void;
  onContactChange?: (contact: ContactInfo) => void;
  onCompanyChange?: (company: CompanyInfo) => void;
  onBillingChange?: (billing: BillingInfo) => void;
}

const COUNTRIES = [
  { value: "SE", label: "Sverige" },
  { value: "NO", label: "Norge" },
  { value: "DK", label: "Danmark" },
  { value: "FI", label: "Finland" },
];

function Section({
  icon: Icon,
  title,
  sectionKey,
  editingSection,
  onEditSection,
  children,
  editContent,
}: {
  icon: React.ElementType;
  title: string;
  sectionKey: EditableSection;
  editingSection?: EditableSection;
  onEditSection?: (section: EditableSection) => void;
  children: React.ReactNode;
  editContent?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const isEditing = editingSection === sectionKey;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5 text-primary" />
          <h3 className="text-base font-semibold text-foreground">{title}</h3>
        </div>
        {onEditSection && !isEditing && (
          <button
            onClick={() => onEditSection(sectionKey)}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {isEditing && editContent ? (
        <div className="space-y-4">
          {editContent}
          <Button
            size="sm"
            onClick={() => onEditSection?.(null)}
            className="gap-1.5"
          >
            <Check className="h-3.5 w-3.5" />
            {t("order.saveSection")}
          </Button>
        </div>
      ) : (
        <div className="bg-muted/30 rounded-lg p-4 space-y-2 text-sm">{children}</div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground font-medium text-right">{value}</span>
    </div>
  );
}

function ContactEditForm({ contact, onChange }: { contact: ContactInfo; onChange: (c: ContactInfo) => void }) {
  const { t } = useTranslation();
  return (
    <div className="grid sm:grid-cols-2 gap-4 p-4 rounded-lg border border-border bg-card">
      <div className="space-y-1.5">
        <Label>{t("order.firstName")} *</Label>
        <Input value={contact.firstName} onChange={(e) => onChange({ ...contact, firstName: e.target.value })} />
      </div>
      <div className="space-y-1.5">
        <Label>{t("order.lastName")} *</Label>
        <Input value={contact.lastName} onChange={(e) => onChange({ ...contact, lastName: e.target.value })} />
      </div>
      <div className="space-y-1.5">
        <Label>{t("order.phone")} *</Label>
        <Input value={contact.phone} onChange={(e) => onChange({ ...contact, phone: e.target.value })} />
      </div>
      <div className="space-y-1.5">
        <Label>{t("order.email")} *</Label>
        <Input type="email" value={contact.email} onChange={(e) => onChange({ ...contact, email: e.target.value })} />
      </div>
    </div>
  );
}

function CompanyEditForm({ company, onChange }: { company: CompanyInfo; onChange: (c: CompanyInfo) => void }) {
  const { t } = useTranslation();
  return (
    <div className="grid sm:grid-cols-2 gap-4 p-4 rounded-lg border border-border bg-card">
      <div className="space-y-1.5">
        <Label>{t("order.companyName")} *</Label>
        <Input value={company.companyName} onChange={(e) => onChange({ ...company, companyName: e.target.value })} />
      </div>
      <div className="space-y-1.5">
        <Label>{t("order.orgNumber")} *</Label>
        <Input value={company.orgNumber} onChange={(e) => onChange({ ...company, orgNumber: e.target.value })} placeholder="0000000000" maxLength={10} />
      </div>
      <div className="space-y-1.5 sm:col-span-2">
        <Label>{t("order.address")} *</Label>
        <Input value={company.address} onChange={(e) => onChange({ ...company, address: e.target.value })} />
      </div>
      <div className="space-y-1.5">
        <Label>{t("order.postalCode")} *</Label>
        <Input value={company.postalCode} onChange={(e) => onChange({ ...company, postalCode: e.target.value })} />
      </div>
      <div className="space-y-1.5">
        <Label>{t("order.city")} *</Label>
        <Input value={company.city} onChange={(e) => onChange({ ...company, city: e.target.value })} />
      </div>
      <div className="space-y-1.5">
        <Label>{t("order.country")} *</Label>
        <select
          value={company.country}
          onChange={(e) => onChange({ ...company, country: e.target.value })}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {COUNTRIES.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

function BillingEditForm({ billing, company, onChange }: { billing: BillingInfo; company: CompanyInfo; onChange: (b: BillingInfo) => void }) {
  const { t } = useTranslation();
  const copyFromCompany = () => {
    onChange({ ...billing, address: company.address, postalCode: company.postalCode, city: company.city, country: company.country });
  };
  return (
    <div className="space-y-4 p-4 rounded-lg border border-border bg-card">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={copyFromCompany} className="gap-1.5">
          <Copy className="h-3.5 w-3.5" />
          {t("order.copyFromCompany")}
        </Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5 sm:col-span-2">
          <Label>{t("order.billingAddress")} *</Label>
          <Input value={billing.address} onChange={(e) => onChange({ ...billing, address: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label>{t("order.postalCode")} *</Label>
          <Input value={billing.postalCode} onChange={(e) => onChange({ ...billing, postalCode: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label>{t("order.city")} *</Label>
          <Input value={billing.city} onChange={(e) => onChange({ ...billing, city: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label>{t("order.country")} *</Label>
          <select
            value={billing.country}
            onChange={(e) => onChange({ ...billing, country: e.target.value })}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {COUNTRIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label>{t("order.reference")}</Label>
          <Input value={billing.reference} onChange={(e) => onChange({ ...billing, reference: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label>{t("order.poNumber")}</Label>
          <Input value={billing.poNumber} onChange={(e) => onChange({ ...billing, poNumber: e.target.value })} />
        </div>
        <div className="space-y-3 sm:col-span-2">
          <Label className="text-base font-semibold">{t("order.invoiceMethod")} *</Label>
          <RadioGroup
            value={billing.invoiceMethod}
            onValueChange={(v) => onChange({ ...billing, invoiceMethod: v as BillingInfo["invoiceMethod"] })}
            className="flex gap-6"
          >
            {(["email", "einvoice", "letter"] as const).map((method) => (
              <div key={method} className="flex items-center gap-2">
                <RadioGroupItem value={method} id={`review-invoice-${method}`} />
                <Label htmlFor={`review-invoice-${method}`} className="font-normal cursor-pointer">
                  {t(`order.invoiceMethods.${method}`)}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>
        {billing.invoiceMethod === "email" && (
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t("order.invoiceEmail")} *</Label>
            <Input type="email" value={billing.invoiceEmail} onChange={(e) => onChange({ ...billing, invoiceEmail: e.target.value })} />
          </div>
        )}
        {billing.invoiceMethod === "einvoice" && (
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t("order.peppolId")} *</Label>
            <Input value={billing.invoiceEmail} onChange={(e) => onChange({ ...billing, invoiceEmail: e.target.value })} />
          </div>
        )}
        <div className="space-y-1.5">
          <Label>{t("order.vatNumber")}</Label>
          <Input value={billing.vatNumber} onChange={(e) => onChange({ ...billing, vatNumber: e.target.value })} />
        </div>
      </div>
    </div>
  );
}

export function OrderReview({
  product,
  atlasConfig,
  validConfig,
  contact,
  company,
  billing,
  adminUsers,
  viewerUsers,
  showTerms,
  termsAccepted,
  onTermsChange,
  editingSection,
  onEditSection,
  onAtlasConfigChange,
  onValidConfigChange,
  onAdminUsersChange,
  onViewerUsersChange,
  onContactChange,
  onCompanyChange,
  onBillingChange,
}: Props) {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguage();

  const COUNTRY_LABELS: Record<string, string> = { se: "Sverige", no: "Norge", dk: "Danmark", fi: "Finland" };
  const BILLING_COUNTRY: Record<string, string> = { SE: "Sverige", NO: "Norge", DK: "Danmark", FI: "Finland" };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-semibold text-foreground">{t("order.reviewTitle")}</h2>
        <p className="text-muted-foreground mt-1">{t("order.reviewDesc")}</p>
      </div>

      {/* Product & configuration */}
      <Section
        icon={Package}
        title={t("order.reviewProduct")}
        sectionKey="config"
        editingSection={editingSection}
        onEditSection={onEditSection}
        editContent={
          product === "atlas" && onAtlasConfigChange ? (
            <OrderAtlasConfig config={atlasConfig} onChange={onAtlasConfigChange} />
          ) : product === "valid" && onValidConfigChange ? (
            <OrderValidConfig config={validConfig} onChange={onValidConfigChange} />
          ) : undefined
        }
      >
        <Row label={t("order.reviewProductLabel")} value={product.toUpperCase()} />
        {product === "atlas" ? (
          <>
            <Row label={t("order.choosePlan")} value={t(`productPages.atlas.pricing.plans.${atlasConfig.plan}.name`)} />
            <Row label={t("order.adminUsers")} value={String(atlasConfig.adminUsers)} />
            <Row label={t("order.viewerUsers")} value={String(atlasConfig.viewerUsers)} />
            <Row label={t("order.maps")} value={String(atlasConfig.maps)} />
            <Row label={t("order.countries.title")} value={atlasConfig.countries.map(c => COUNTRY_LABELS[c] || c.toUpperCase()).join(", ")} />
            {atlasConfig.routing !== "none" && (
              <Row label={t("order.routing.title")} value={t(`order.routing.tiers.${atlasConfig.routing}.name`)} />
            )}
          </>
        ) : (
          <>
            <Row label={t("order.choosePlan")} value={validConfig.plan.toUpperCase()} />
            {validConfig.addons.length > 0 && (
              <Row label={t("order.addons")} value={validConfig.addons.join(", ")} />
            )}
          </>
        )}
      </Section>

      {/* Users (Atlas) */}
      {product === "atlas" && (adminUsers.length > 0 || viewerUsers.length > 0) && (
        <Section
          icon={Users}
          title={t("order.userDetails")}
          sectionKey="users"
          editingSection={editingSection}
          onEditSection={onEditSection}
          editContent={
            onAdminUsersChange && onViewerUsersChange ? (
              <OrderUserDetails
                adminCount={atlasConfig.adminUsers}
                viewerCount={atlasConfig.viewerUsers}
                adminUsers={adminUsers}
                viewerUsers={viewerUsers}
                onChangeAdmin={onAdminUsersChange}
                onChangeViewer={onViewerUsersChange}
              />
            ) : undefined
          }
        >
          {adminUsers.map((u, i) => (
            <Row key={`a${i}`} label={`${t("order.adminUser")} ${i + 1}`} value={`${u.name} – ${u.email}`} />
          ))}
          {viewerUsers.map((u, i) => (
            <Row key={`v${i}`} label={`${t("order.viewerUser")} ${i + 1}`} value={`${u.name} – ${u.email}`} />
          ))}
        </Section>
      )}

      {/* Web portal users (Valid) */}
      {product === "valid" && validConfig.webPortal && validConfig.webPortalUsers.length > 0 && (
        <Section icon={Users} title={t("order.webPortal.label")} sectionKey="users" editingSection={editingSection} onEditSection={onEditSection}>
          {validConfig.webPortalUsers.map((u, i) => (
            <Row key={`wp${i}`} label={`${i + 1}.`} value={`${u.name} – ${u.email}${u.role ? ` (${u.role})` : ""}`} />
          ))}
        </Section>
      )}

      {/* Contact */}
      <Section
        icon={User}
        title={t("order.contactInfo")}
        sectionKey="contact"
        editingSection={editingSection}
        onEditSection={onEditSection}
        editContent={
          onContactChange ? (
            <ContactEditForm contact={contact} onChange={onContactChange} />
          ) : undefined
        }
      >
        <Row label={t("order.firstName")} value={contact.firstName} />
        <Row label={t("order.lastName")} value={contact.lastName} />
        <Row label={t("order.phone")} value={contact.phone} />
        <Row label={t("order.email")} value={contact.email} />
      </Section>

      {/* Company */}
      <Section
        icon={Building2}
        title={t("order.companyInfo")}
        sectionKey="company"
        editingSection={editingSection}
        onEditSection={onEditSection}
        editContent={
          onCompanyChange ? (
            <CompanyEditForm company={company} onChange={onCompanyChange} />
          ) : undefined
        }
      >
        <Row label={t("order.companyName")} value={company.companyName} />
        <Row label={t("order.orgNumber")} value={company.orgNumber} />
        <Row label={t("order.address")} value={`${company.address}, ${company.postalCode} ${company.city}`} />
        <Row label={t("order.country")} value={BILLING_COUNTRY[company.country] || company.country} />
      </Section>

      {/* Billing */}
      <Section
        icon={FileText}
        title={t("order.billingInfo")}
        sectionKey="billing"
        editingSection={editingSection}
        onEditSection={onEditSection}
        editContent={
          onBillingChange ? (
            <BillingEditForm billing={billing} company={company} onChange={onBillingChange} />
          ) : undefined
        }
      >
        <Row label={t("order.billingAddress")} value={`${billing.address}, ${billing.postalCode} ${billing.city}`} />
        <Row label={t("order.invoiceMethod")} value={t(`order.invoiceMethods.${billing.invoiceMethod}`)} />
        {billing.invoiceEmail && <Row label={t("order.invoiceEmail")} value={billing.invoiceEmail} />}
        {billing.reference && <Row label={t("order.reference")} value={billing.reference} />}
        {billing.poNumber && <Row label={t("order.poNumber")} value={billing.poNumber} />}
        {billing.vatNumber && <Row label={t("order.vatNumber")} value={billing.vatNumber} />}
      </Section>

      <div className="flex items-start gap-3 p-4 rounded-xl border border-primary/30 bg-primary/5">
        <CheckCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
        <p className="text-sm text-foreground">{t("order.reviewConfirmNote")}</p>
      </div>

      {showTerms && onTermsChange && (
        <div className="flex items-start gap-3 p-4 rounded-xl border border-border bg-card">
          <Checkbox
            id="review-terms"
            checked={termsAccepted}
            onCheckedChange={(checked) => onTermsChange(checked === true)}
            className="mt-0.5"
          />
          <Label htmlFor="review-terms" className="text-sm font-normal leading-relaxed cursor-pointer">
            {t("order.termsPrefix")}{" "}
            <Link to={getLocalizedPath("/information/villkor")} target="_blank" className="text-primary underline hover:no-underline">
              {t("order.termsLink")}
            </Link>
          </Label>
        </div>
      )}
    </div>
  );
}
